import { useEffect, useRef } from 'react';

const useWheelHorizontalScroll = () => {
  const ref = useRef(null);
  const targetScrollLeft = useRef(0);
  const rafId = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const animate = () => {
      const diff = targetScrollLeft.current - el.scrollLeft;
      if (Math.abs(diff) <= 1) {
        el.scrollLeft = targetScrollLeft.current;
        rafId.current = null;
        return;
      }
      el.scrollLeft += diff * 0.12;
      rafId.current = requestAnimationFrame(animate);
    };

    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();

      const maxScroll = el.scrollWidth - el.clientWidth;
      const target = Math.max(0, Math.min(maxScroll, el.scrollLeft + e.deltaY));

      // Only snap immediately when already near the edge — avoids janky
      // animation cancellation mid-scroll while still ensuring we land exactly
      // on the boundary when close enough that momentum events would fight us.
      const nearEdge = (target === 0 && el.scrollLeft <= 15) ||
                       (target === maxScroll && el.scrollLeft >= maxScroll - 15);

      if (nearEdge) {
        if (rafId.current !== null) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
        el.scrollLeft = target;
        targetScrollLeft.current = target;
        return;
      }

      targetScrollLeft.current = target;
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(animate);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return ref;
};

export default useWheelHorizontalScroll;
